/**
 * LoginPage — premium dual-tab auth (Sign in / Sign up).
 *
 * Highlights
 * ──────────
 * • Obsidian Night aesthetic: animated particle canvas background,
 *   conic gradient orb, glassmorphism card.
 * • Sign up gated to @wireless-zone.com emails with a live email-validity ring
 *   and a 5-step password strength meter.
 * • Session duration is decided server-side based on role (user=24h,
 *   manager=7d, admin=30d) — this page just POSTs credentials.
 * • Preserves Emergent-managed Google OAuth as the default primary sign-in.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ConnectionSourceLogo from "../components/ConnectionSourceLogo";

const ALLOWED_DOMAIN = "wireless-zone.com";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login: doLogin, register: doRegister } = useAuth();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'

  // OAuth return handled in AuthCallback — nothing to do here.

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent(window.location.origin + "/login");
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  return (
    <div className="cs-login-root min-h-screen relative overflow-hidden"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <AmbientBackground />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8 gap-6">
        <div className="cs-login-card relative w-full max-w-md rounded-3xl overflow-hidden">
          {/* Top shimmer */}
          <div
            aria-hidden
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: "linear-gradient(90deg, transparent, #CD040B, #F59E0B, #CD040B, transparent)" }}
          />
          <div className="cs-login-inner relative p-7">
          <div className="flex justify-center mb-5">
            <ConnectionSourceLogo size="md" />
          </div>

          <div className="text-center mb-5">
            <h1
              className="text-2xl font-black leading-tight mb-1 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}
            >
              {mode === "signup" ? "Join the team" : "Welcome back"}
            </h1>
            <p className="text-xs" style={{ color: "var(--cs-text-muted)" }}>
              {mode === "signup"
                ? `Sign up with your @${ALLOWED_DOMAIN} email to unlock the workspace.`
                : "Sign in to track sales, pay and performance."}
            </p>
          </div>

          <AuthModeTabs mode={mode} onChange={setMode} />

          <div className="mt-5">
            {mode === "signin"
              ? <SignInForm doLogin={doLogin} navigate={navigate} />
              : <SignUpForm doRegister={doRegister} navigate={navigate} />}
          </div>

          <Divider label="or continue with" />

          <button
            type="button"
            onClick={handleGoogleLogin}
            className="cs-btn-google w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2.5"
            data-testid="google-login-btn"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <p className="text-[10px] text-center mt-5 leading-relaxed" style={{ color: "var(--cs-text-muted)" }}>
            By continuing you agree to our acceptable-use policy. Sign-ups are restricted to <strong>@{ALLOWED_DOMAIN}</strong> addresses.
          </p>
          </div>
        </div>

        {/* Onboarding banner — full-width, artistic, mobile-friendly */}
        <button
          type="button"
          onClick={() => navigate("/onboarding")}
          className="cs-onboarding-banner relative w-full max-w-md rounded-3xl overflow-hidden group"
          data-testid="onboarding-link"
        >
          {/* Gradient base */}
          <div className="cs-onboarding-bg absolute inset-0" aria-hidden />
          {/* Animated shimmer */}
          <div className="cs-onboarding-shimmer absolute inset-0" aria-hidden />
          {/* Decorative blob */}
          <div className="cs-onboarding-blob absolute" aria-hidden />

          <div className="relative z-10 flex items-center gap-4 sm:gap-5 p-5 sm:p-6 text-left">
            {/* Avatar / icon stack */}
            <div className="cs-onboarding-icon shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <div className="cs-onboarding-eyebrow">
                New Employee Registration?
              </div>
              <div className="cs-onboarding-headline">
                Start your onboarding
              </div>
              <div className="cs-onboarding-sub hidden sm:block">
                Quick application · 4 short steps · Encrypted from end to end
              </div>
              <div className="cs-onboarding-sub block sm:hidden">
                Quick · Encrypted · 4 steps
              </div>
            </div>

            <div className="cs-onboarding-cta shrink-0">
              <span className="hidden sm:inline">Begin</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────── Tabs ─────────────────────── */

function AuthModeTabs({ mode, onChange }) {
  return (
    <div className="cs-auth-tabs relative rounded-xl p-1 flex" data-testid="auth-mode-tabs">
      <span
        className="cs-auth-tab-indicator"
        style={{ transform: `translateX(${mode === "signup" ? "100%" : "0%"})` }}
        aria-hidden
      />
      {[
        { id: "signin", label: "Sign in" },
        { id: "signup", label: "Create account" },
      ].map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`cs-auth-tab relative z-10 flex-1 py-2.5 text-[12px] font-bold tracking-wide ${mode === t.id ? "active" : ""}`}
          data-testid={`auth-tab-${t.id}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────── Sign in ─────────────────────── */

function SignInForm({ doLogin, navigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const result = await doLogin(email.trim().toLowerCase(), password);
    setLoading(false);
    if (result.success) {
      toast.success("Welcome back!");
      navigate("/");
    } else {
      toast.error(result.error || "Login failed");
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="signin-form">
      <FloatingInput
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        testid="signin-email-input"
      />
      <FloatingInput
        label="Password"
        type={show ? "text" : "password"}
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        right={<button type="button" onClick={() => setShow((s) => !s)} className="text-[10px] font-bold cs-eye-btn">{show ? "HIDE" : "SHOW"}</button>}
        testid="signin-password-input"
      />
      <SubmitButton loading={loading} label="Sign in" loadingLabel="Signing in…" testid="signin-submit" />
      <div className="pt-1 text-center">
        <button
          type="button"
          onClick={() => navigate("/forgot-password")}
          className="cs-forgot-link text-[11px] font-semibold tracking-wide"
          data-testid="forgot-password-link"
        >
          Forgot your password?
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────── Sign up ─────────────────────── */

function SignUpForm({ doRegister, navigate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const domainOk = useMemo(
    () => /.+@wireless-zone\.com$/i.test(email.trim()),
    [email]
  );
  const strength = useMemo(() => computeStrength(password), [password]);
  const canSubmit = name.trim().length >= 2 && domainOk && strength.score >= 4 && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    const result = await doRegister(email.trim().toLowerCase(), password, name.trim());
    setLoading(false);
    if (result.success) {
      confettiBurst();
      toast.success("You're in!");
      navigate("/");
    } else {
      toast.error(result.error || "Sign-up failed");
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="signup-form">
      <FloatingInput
        label="Your name"
        value={name}
        onChange={setName}
        autoComplete="name"
        testid="signup-name-input"
      />
      <FloatingInput
        label={`Work email (@${ALLOWED_DOMAIN})`}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        state={email ? (domainOk ? "ok" : "bad") : "neutral"}
        hint={email && !domainOk ? `Only @${ALLOWED_DOMAIN} emails are allowed.` : null}
        testid="signup-email-input"
      />
      <FloatingInput
        label="Create a password"
        type={show ? "text" : "password"}
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        right={<button type="button" onClick={() => setShow((s) => !s)} className="text-[10px] font-bold cs-eye-btn">{show ? "HIDE" : "SHOW"}</button>}
        testid="signup-password-input"
      />
      <StrengthMeter strength={strength} />
      <SubmitButton loading={loading} disabled={!canSubmit} label="Create account" loadingLabel="Creating…" testid="signup-submit" />
    </form>
  );
}

/* ─────────────────────── Strength meter ─────────────────────── */

function computeStrength(pw) {
  const checks = [
    { id: "length",    ok: pw.length >= 10, label: "10+ characters" },
    { id: "lower",     ok: /[a-z]/.test(pw), label: "lowercase" },
    { id: "upper",     ok: /[A-Z]/.test(pw), label: "uppercase" },
    { id: "number",    ok: /\d/.test(pw), label: "number" },
    { id: "symbol",    ok: /[^A-Za-z0-9]/.test(pw), label: "symbol" },
  ];
  const score = checks.filter(c => c.ok).length;
  return { score, checks };
}

function StrengthMeter({ strength }) {
  const { score, checks } = strength;
  const labels = ["Add a password", "Very weak", "Weak", "Okay", "Strong", "Rock-solid"];
  const colors = ["#52525B", "#EF4444", "#F97316", "#F59E0B", "#10B981", "#059669"];
  return (
    <div className="mt-2" data-testid="signup-strength">
      <div className="flex gap-1" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{
              background: i < score ? colors[score] : "var(--cs-border)",
              boxShadow: i < score ? `0 0 8px ${colors[score]}55` : "none",
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[10px] font-bold" style={{ color: colors[score] }}>
          {labels[score]}
        </span>
        <span className="text-[10px] tabular-nums" style={{ color: "var(--cs-text-muted)" }}>
          {score}/5
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {checks.map((c) => (
          <span
            key={c.id}
            className="text-[9px] font-bold px-2 py-0.5 rounded-full transition-all"
            style={{
              background: c.ok ? "rgba(16,185,129,0.14)" : "var(--cs-bg-hover)",
              color: c.ok ? "#10B981" : "var(--cs-text-muted)",
              border: c.ok ? "1px solid rgba(16,185,129,0.4)" : "1px solid var(--cs-border)",
            }}
          >
            {c.ok ? "✓" : "•"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────── Floating input ─────────────────────── */

function FloatingInput({ label, type = "text", value, onChange, right, state, hint, autoComplete, testid }) {
  const focused = value.length > 0;
  const ring = state === "ok" ? "#10B981" : state === "bad" ? "#EF4444" : "#CD040B";
  return (
    <div>
      <label className="cs-floating-field block relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testid}
          className="cs-floating-input peer w-full px-4 pt-5 pb-2 rounded-xl outline-none text-sm transition-all"
          style={{
            background: "var(--cs-bg-hover)",
            border: `1px solid var(--cs-border)`,
            color: "var(--cs-text)",
            "--ring-color": ring,
          }}
        />
        <span
          className={`cs-floating-label absolute left-4 pointer-events-none transition-all ${focused ? "is-up" : ""}`}
        >
          {label}
        </span>
        {right && <span className="absolute right-3 top-1/2 -translate-y-1/2">{right}</span>}
      </label>
      {hint && (
        <div className="text-[10px] mt-1 ml-1 font-semibold" style={{ color: "#EF4444" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Submit button ─────────────────────── */

function SubmitButton({ loading, disabled, label, loadingLabel, testid }) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black flex items-center justify-center gap-2 overflow-hidden relative"
      data-testid={testid}
    >
      <span className="relative z-10" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>
        {loading ? loadingLabel : label}
      </span>
      {loading && <span className="cs-spinner" aria-hidden />}
    </button>
  );
}

/* ─────────────────────── Misc UI ─────────────────────── */

function Divider({ label }) {
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="flex-1 h-px" style={{ background: "var(--cs-border)" }} />
      <span className="text-[10px] font-bold uppercase tracking-[2px]" style={{ color: "var(--cs-text-muted)" }}>
        {label}
      </span>
      <span className="flex-1 h-px" style={{ background: "var(--cs-border)" }} />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || "").filter(Boolean).join(" · ");
  return err?.message || null;
}

/* ─────────────────────── Ambient background ─────────────────────── */

function AmbientBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // S25 Ultra has DPR ≈ 3.5; rasterising the canvas at that density is
    // what triggers the Vulkan compositor flicker.  Cap DPR more tightly
    // on touch devices and cut particle counts so the per-frame work stays
    // under the 8 ms budget required for 120 Hz refresh.
    //
    // Same gate as the nuclear CSS override in index.css — touch OR PWA
    // standalone mode on a phone-sized viewport.  PWA mode on Chrome
    // Android does NOT always match `(hover: none)` so we union triggers.
    const matches = (q) => { try { return window.matchMedia?.(q).matches; } catch { return false; } };
    const isTouch = matches('(hover: none) and (pointer: coarse)')
      || (matches('(display-mode: standalone)') && matches('(max-width: 900px)'))
      || matches('(max-width: 640px)');
    // Skip the canvas entirely on touch — the static gradient background
    // already gives plenty of depth and the canvas is the single biggest
    // GPU-pressure source on the login page.
    if (isTouch) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let rafId;
    // Rotating palette for colorful particles
    const PALETTE = [
      [205, 4, 11],    // crimson
      [245, 158, 11],  // amber
      [139, 92, 246],  // violet
      [6, 182, 212],   // cyan
      [236, 72, 153],  // pink
    ];
    const particles = [];

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    resize();
    window.addEventListener("resize", resize);

    // 70 particles on desktop only — canvas is fully bypassed on touch.
    for (let i = 0; i < 70; i++) {
      const palette = PALETTE[i % PALETTE.length];
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.45 * dpr,
        vy: (Math.random() - 0.5) * 0.45 * dpr,
        r: (Math.random() * 1.8 + 0.5) * dpr,
        color: palette,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const now = performance.now() / 1000;

      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        const twinkle = 0.55 + 0.45 * Math.sin(now * 2 + p.pulse);
        const [r, g, b] = p.color;
        // glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 10);
        grad.addColorStop(0, `rgba(${r},${g},${b},${0.9 * twinkle})`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 10, 0, Math.PI * 2);
        ctx.fill();
        // core
        ctx.fillStyle = `rgba(${r},${g},${b},${twinkle})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // Connecting lines between nearby particles, colored by blend
      const maxDist = 160 * dpr;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < maxDist) {
            const alpha = 0.22 * (1 - d / maxDist);
            const r = Math.round((a.color[0] + b.color[0]) / 2);
            const g = Math.round((a.color[1] + b.color[1]) / 2);
            const bb = Math.round((a.color[2] + b.color[2]) / 2);
            ctx.strokeStyle = `rgba(${r},${g},${bb},${alpha})`;
            ctx.lineWidth = 1 * dpr;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      <div aria-hidden className="cs-login-aurora" />
      <div aria-hidden className="cs-login-orb" />
      <div aria-hidden className="cs-login-grid" />
      <div aria-hidden className="cs-login-scanner" />
      <canvas ref={canvasRef} className="cs-login-canvas fixed inset-0 pointer-events-none" aria-hidden />
      {/* Floating geometric shapes */}
      <svg aria-hidden className="cs-login-shape s1" width="120" height="120" viewBox="0 0 120 120">
        <polygon points="60,8 112,32 112,88 60,112 8,88 8,32" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <polygon points="60,28 92,44 92,76 60,92 28,76 28,44" fill="none" stroke="currentColor" strokeWidth="1" />
      </svg>
      <svg aria-hidden className="cs-login-shape s2" width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r="38" fill="none" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="45" cy="45" r="26" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 3" />
        <circle cx="45" cy="45" r="4" fill="currentColor" />
      </svg>
      <svg aria-hidden className="cs-login-shape s3" width="140" height="140" viewBox="0 0 140 140">
        <rect x="24" y="24" width="92" height="92" fill="none" stroke="currentColor" strokeWidth="1" transform="rotate(15 70 70)" />
        <rect x="40" y="40" width="60" height="60" fill="none" stroke="currentColor" strokeWidth="1" transform="rotate(-15 70 70)" />
      </svg>
      <svg aria-hidden className="cs-login-shape s4" width="100" height="100" viewBox="0 0 100 100">
        <polygon points="50,10 90,50 50,90 10,50" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <polygon points="50,28 72,50 50,72 28,50" fill="none" stroke="currentColor" strokeWidth="1" />
      </svg>
    </>
  );
}

/* ─────────────────────── Confetti burst ─────────────────────── */

function confettiBurst() {
  try {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = document.createElement("div");
    root.setAttribute("aria-hidden", "true");
    root.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
    document.body.appendChild(root);
    const colors = ["#CD040B", "#F59E0B", "#10B981", "#6366F1", "#EC4899"];
    for (let i = 0; i < 40; i++) {
      const el = document.createElement("span");
      const c = colors[i % colors.length];
      el.style.cssText = `position:absolute;top:55%;left:50%;width:8px;height:14px;background:${c};border-radius:2px;opacity:0.95;transform:translate(-50%,-50%) rotate(${Math.random()*360}deg);`;
      const angle = Math.random() * Math.PI * 2;
      const distance = 120 + Math.random() * 320;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance + 160;
      el.animate(
        [
          { transform: `translate(-50%,-50%) rotate(0deg)`, opacity: 1 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${720 + Math.random()*360}deg)`, opacity: 0 },
        ],
        { duration: 1100 + Math.random() * 500, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "forwards" }
      );
      root.appendChild(el);
    }
    setTimeout(() => root.remove(), 2000);
  } catch { /* noop */ }
}
